import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 버튼에 붙는 작은 메뉴의 자리를 정한다 (2026-08-26).
 *
 * 왜 필요한가: 채팅에서 메뉴를 버튼 옆에 `position:absolute` 로 뒀다가 두 번 데었다.
 *   · 대화 목록이 `overflow-y:auto` 라 메뉴가 **잘려서 안 보였고**,
 *   · 입력창은 화면 맨 아래라 아래로 연 메뉴가 **창 밖으로 나갔다**.
 * 그래서 메뉴는 body 로 띄우고(`position:fixed`) **버튼 자리를 재서** 좌표를 정한다.
 * 아래가 모자라면 위로, 옆이 모자라면 안쪽으로 당긴다.
 *
 * 스크롤·창 크기 변경에는 좌표가 어긋난다 — 따라다니게 만들기보다 **닫는다**(흔한 처리).
 * Esc 로도 닫힌다.
 */

const EDGE = 8;
const GAP = 6;

export interface AnchoredMenu<T extends HTMLElement> {
  /** 메뉴를 여는 버튼에 붙인다. */
  anchorRef: React.RefObject<T>;
  /**
   * 열려 있으면 좌표, 닫혀 있으면 null.
   * ★ `anchorWidth` 는 **연 순간의 버튼 폭**이다 — 「버튼과 같은 폭으로」 그리려는 자리가
   *   쓴다(2026-09-06). 안 쓰는 곳은 그냥 무시하면 된다(더한 칸이라 회귀가 없다).
   */
  pos: { top: number; left: number; anchorWidth: number } | null;
  open: boolean;
  toggle: () => void;
  close: () => void;
}

export interface AnchoredMenuOptions {
  /**
   * 가로 맞춤 — `end`(기본)는 **버튼 오른쪽 끝**에, `start` 는 **버튼 왼쪽 끝**에 맞춘다.
   * ★ 좁은 레일에서 버튼 바로 아래로 펼치는 자리는 `start` 다(오른쪽 맞춤이면 버튼과
   *   어긋나 보인다). 기본을 그대로 둔 것은 이미 쓰는 두 곳을 안 움직이려는 것이다.
   */
  align?: 'start' | 'end';
  /**
   * 폭을 **버튼에 맞춘다** — 열 때 잰 버튼 폭을 `size.width` 대신 쓴다(넘침 판정에도).
   * 레일처럼 「버튼 아래에 같은 폭으로」 펼치는 자리를 위한 것.
   */
  matchAnchorWidth?: boolean;
}

export function useAnchoredMenu<T extends HTMLElement>(
  /** 메뉴 상자 크기 — 좌표를 미리 재려면 알아야 한다. CSS 와 함께 고친다. */
  size: { width: number; height: number },
  options: AnchoredMenuOptions = {},
): AnchoredMenu<T> {
  const anchorRef = useRef<T>(null);
  const [pos, setPos] = useState<{ top: number; left: number; anchorWidth: number } | null>(null);
  const align = options.align ?? 'end';
  const matchAnchorWidth = options.matchAnchorWidth ?? false;

  const close = useCallback(() => setPos(null), []);

  const toggle = useCallback(() => {
    setPos((cur) => {
      if (cur) return null;
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return null;
      const width = matchAnchorWidth ? r.width : size.width;
      // 기본은 버튼 오른쪽 끝에 맞춰 아래로. 넘치면 안쪽으로 당긴다.
      let left = align === 'start' ? r.left : r.right - width;
      left = Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE));
      let top = r.bottom + GAP;
      if (top + size.height > window.innerHeight - EDGE) {
        // 아래가 모자라면 버튼 위로. 위도 모자라면 창 안에서 최대한 내린다.
        top = Math.max(EDGE, r.top - size.height - GAP);
      }
      return { top, left, anchorWidth: r.width };
    });
  }, [size.width, size.height, align, matchAnchorWidth]);

  useEffect(() => {
    if (!pos) return;
    const onMove = () => close();
    /**
     * ★★★ Esc 는 **이 메뉴만** 닫는다 — **capture 단계**로 받는다 (2026-09-16).
     *
     *   공용 `Modal` 의 `useEscapeClose` 도 window 에서 Escape 를 받고, 2026-09-15 부터
     *   **누가 먼저 처리했는지**(`defaultPrevented`)를 본다. 그런데 `preventDefault()` 만 더하는 것으로는
     *   **부족하다** — 둘 다 window 의 **bubble** 단계면 «등록 순서»로 불리고, 모달이 먼저 마운트되므로
     *   **모달 것이 먼저 돌아 창을 닫아 버린다**(그때는 아직 defaultPrevented 가 false 다).
     *   그래서 여기서는 **capture** 로 걸어 모달보다 **먼저** 받고, 거기서 표시를 남긴다.
     *   ★ 공용 `DropdownMenu` 가 멀쩡했던 이유도 같다 — 그쪽은 window 가 아니라 **메뉴 엘리먼트의
     *     onKeyDown** 이라 window 리스너보다 앞서 돈다.
     *   ⚠ 고치기 전 증상: 메뉴와 창이 **함께 닫혔다**(확인된 자리 — 채팅 미디어 라이트박스 안의 저장 메뉴).
     * ★ 이 리스너는 **열려 있을 때만** 붙는다(위 `if (!pos) return`) — 닫혀 있으면 아무것도 먹지 않아
     *   메뉴가 없을 때의 모달 Esc 는 종전 그대로다(`Modal.escape.test.tsx` ④ 의 둘째 검사).
     */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      close();
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [pos, close]);

  return { anchorRef, pos, open: !!pos, toggle, close };
}
